import { useState, VFC } from "react";

import { Button, ButtonGroup, Checkbox, FormControlLabel, portexColor, TextInput } from "@portex-pro/ui-components";
import { ShipperQuoteRequest, useUpdateShipperQuoteRequestMutation } from "api/rest/quote-requests";
import PortexDialog from "components/PortexDialog";
import Text from "components/Text";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

interface CloseQuoteRequestDialogProps {
  quoteRequest: ShipperQuoteRequest;
  open: boolean;
  onClose: () => void;
}

const CloseQuoteRequestDialog: VFC<CloseQuoteRequestDialogProps> = (props) => {
  const { quoteRequest, open, onClose } = props;
  const { t } = useTranslation(["shipper", "common"]);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedReason, setSelectedReason] = useState<"BOOKED_OFF_PLATFORM" | "OTHER">("BOOKED_OFF_PLATFORM");
  const [otherReason, setOtherReason] = useState<string | null>(null);
  const [shouldSendNotification, setShouldSendNotifcation] = useState(true);

  const [updateQuoteRequest, { isLoading }] = useUpdateShipperQuoteRequestMutation();

  const closeQuoteRequest = async () => {
    try {
      await updateQuoteRequest({
        urlParams: { quoteRequestId: quoteRequest.id },
        body: {
          status: "CLOSED",
          reason:
            selectedReason === "BOOKED_OFF_PLATFORM" ? "BOOKED_OFF_PLATFORM" : otherReason || "No reason provided",
          sendNotification: shouldSendNotification,
        },
      }).unwrap();

      enqueueSnackbar(t("common:closed"), { variant: "success" });
      onClose();
    } catch (e) {
      enqueueSnackbar(t("common:errors.generic"), { variant: "error" });
      onClose();
    }
  };

  return (
    <PortexDialog
      open={open}
      onClose={onClose}
      title={t("shipper:closeQuote")}
      maxWidth="sm"
      confirmButtonProps={{ style: { backgroundColor: portexColor.red500 }, disabled: isLoading }}
      onClickConfirm={closeQuoteRequest}
    >
      <div className="flex flex-col space-y-4 p-6">
        <Text size="medium">{t("shipper:closeQuoteDialog.confirm")}</Text>
        <Text size="medium" weight="bold">
          {t("shipper:closeQuoteDialog.reason")}
        </Text>

        <ButtonGroup fullWidth color="primary">
          <Button
            className={selectedReason === "BOOKED_OFF_PLATFORM" ? "Ptx-selected" : undefined}
            onClick={() => {
              setSelectedReason("BOOKED_OFF_PLATFORM");
              setOtherReason(null);
            }}
          >
            {t("shipper:closeQuoteDialog.bookedOutside")}
          </Button>
          <Button
            className={selectedReason === "OTHER" ? "Ptx-selected" : undefined}
            onClick={() => setSelectedReason("OTHER")}
          >
            {t("shipper:other")}
          </Button>
        </ButtonGroup>

        {selectedReason === "OTHER" && (
          <TextInput
            autoFocus
            multiline
            rows={2}
            fullWidth
            margin="normal"
            value={otherReason ?? ""}
            placeholder={t("shipper:closeQuoteDialog.placeholder")}
            onChange={(event) => setOtherReason(event.target.value)}
          />
        )}

        <FormControlLabel
          className="Por-confirmation"
          control={
            <Checkbox checked={shouldSendNotification} onChange={(e) => setShouldSendNotifcation(e.target.checked)} />
          }
          label={t("shipper:closeQuoteDialog.sendNotification")}
        />
      </div>
    </PortexDialog>
  );
};

export default CloseQuoteRequestDialog;
